/**
 * @file ErrorBoundary.js — Intercepteur d'erreurs React au niveau global.
 * Capture les exceptions non gérées dans l'arbre de composants enfants
 * et affiche une page d'erreur conviviale au lieu d'un écran blanc (R-0124).
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Composant de fallback pour l'erreur 500 — affiche le message détaillé.
 * Séparé en composant fonctionnel pour pouvoir être étendu avec des hooks si besoin.
 */
function ErrorFallback({ error }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground px-4" data-testid="error-500-page">
      <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
      <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>500</h1>
      <p className="text-lg text-muted-foreground mb-1">Internal Error</p>
      <p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
        Something went wrong while rendering this page. This is an unexpected error on our side, not something you did wrong.
      </p>
      {error?.message && (
        <p className="text-xs text-muted-foreground mb-2 text-center max-w-md">
          Details: <code className="px-1 py-0.5 bg-muted rounded break-all">{error.message}</code>
        </p>
      )}
      <p className="text-xs text-muted-foreground mb-6">
        Error code: <code className="px-1 py-0.5 bg-muted rounded">ERR_RENDER_FAILURE</code>
      </p>
      <div className="flex gap-3">
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-medium hover:bg-primary/90 transition-colors"
          data-testid="error-500-reload"
        >
          Reload page
        </button>
        <a
          href="/"
          className="px-4 py-2 border border-border text-foreground rounded-sm text-sm font-medium hover:bg-muted transition-colors"
          data-testid="error-500-home"
        >
          Go to home page
        </a>
      </div>
    </div>
  );
}

/**
 * Composant Error Boundary (classe React requise pour componentDidCatch).
 * Affiche une page d'erreur 500 avec un code d'erreur et un bouton pour recharger.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Erreur capturée :', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}
